import {
  BarChart3,
  Bell,
  Languages,
  Settings,
  type LucideIcon,
  UserX,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BlockedUsersModal } from "@/features/user-setting/components/BlockedUsersModal";
import { DisplayPreferencesModal } from "@/features/user-setting/components/DisplayPreferencesModal";
import { EmailNotificationModal } from "@/features/user-setting/components/EmailNotificationModal";
import { UsageModal } from "@/features/user-setting/components/UsageModal";
import { cn } from "@/lib/utils";

type SettingsModalKey =
  | "notification"
  | "display"
  | "blockedUsers"
  | "usage"
  | null;

interface SidebarUserSettingsMenuProps {
  isCollapsed: boolean;
  variant?: "legacy" | "global-sidebar";
}

export const SidebarUserSettingsMenu = ({
  isCollapsed,
  variant = "legacy",
}: SidebarUserSettingsMenuProps) => {
  const { t } = useTranslation();
  const [activeModal, setActiveModal] = useState<SettingsModalKey>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const openModal = (modal: Exclude<SettingsModalKey, null>) => {
    setActiveModal(modal);
  };

  const settingsItems: Array<{
    key: Exclude<SettingsModalKey, null>;
    icon: LucideIcon;
    label: string;
    trailingLabel?: string;
  }> = [
    { key: "notification", icon: Bell, label: t("common.notification") },
    { key: "display", icon: Languages, label: t("common.displaySettings") },
    { key: "blockedUsers", icon: UserX, label: t("common.blockedUsersOnMenu") },
    { key: "usage", icon: BarChart3, label: t("common.usage") },
  ];

  const isGlobalSidebar = variant === "global-sidebar";
  const isActive = isGlobalSidebar && (isMenuOpen || activeModal !== null);
  const triggerButton = (
    <Button
      type="button"
      variant="ghost"
      size={isCollapsed ? "icon" : "sm"}
      className={
        isGlobalSidebar
          ? `group relative !size-10 shrink-0 overflow-visible rounded-none bg-transparent p-0 shadow-none hover:bg-transparent [@media(max-height:700px)]:!size-9 ${
              isActive ? "text-white" : "text-slate-600"
            }`
          : isCollapsed
            ? "size-8 rounded-md text-gray-500 hover:bg-white/50"
            : cn(
                "h-8 w-auto justify-start rounded-md bg-transparent pl-4 pr-0 text-sm text-text-subtitle shadow-none transition-colors hover:bg-transparent hover:text-variable-collection-main",
              )
      }
      aria-label={t("common.basicSettings")}
      aria-current={isActive ? "page" : undefined}
    >
      {isGlobalSidebar ? (
        <span
          className={`absolute left-0 top-0 z-20 flex size-10 items-center overflow-hidden rounded-r-full shadow-sm transition-[width] duration-200 ease-out group-hover:w-[190px] group-focus-visible:w-[190px] [@media(max-height:700px)]:size-9 ${
            isActive
              ? "bg-rw-main text-white group-hover:bg-rw-main group-focus-visible:bg-rw-main"
              : "bg-white text-slate-600"
          }`}
        >
          <span className="flex size-10 shrink-0 items-center justify-center [@media(max-height:700px)]:size-9">
            <Settings
              className="size-5 [@media(max-height:700px)]:size-[18px]"
              strokeWidth={2.2}
            />
          </span>
          <span className="whitespace-nowrap pr-5 text-sm font-semibold [@media(max-height:700px)]:text-xs">
            {t("common.basicSettings")}
          </span>
        </span>
      ) : (
        <Settings className="size-4" />
      )}
      {!isCollapsed && !isGlobalSidebar && (
        <span className="truncate">{t("common.basicSettings")}</span>
      )}
    </Button>
  );

  return (
    <>
      <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        {isCollapsed && !isGlobalSidebar ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  {triggerButton}
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">
                {t("common.basicSettings")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
        )}
        <DropdownMenuContent
          side={isCollapsed || isGlobalSidebar ? "right" : "top"}
          align={isCollapsed || isGlobalSidebar ? "center" : "start"}
          className="w-[220px]"
        >
          {settingsItems.map((item) => (
            <DropdownMenuItem
              key={item.key}
              onSelect={() => openModal(item.key)}
            >
              <item.icon className="size-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {item.trailingLabel ? (
                <span className="ml-auto max-w-[80px] truncate text-xs text-muted-foreground">
                  {item.trailingLabel}
                </span>
              ) : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <EmailNotificationModal
        isOpen={activeModal === "notification"}
        onClose={() => setActiveModal(null)}
      />
      <DisplayPreferencesModal
        isOpen={activeModal === "display"}
        onClose={() => setActiveModal(null)}
      />
      <BlockedUsersModal
        isOpen={activeModal === "blockedUsers"}
        onClose={() => setActiveModal(null)}
      />
      <UsageModal
        isOpen={activeModal === "usage"}
        onClose={() => setActiveModal(null)}
      />
    </>
  );
};
